import {
  Component,
  input,
  output,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';

export type ViewType = 'table' | 'grid';

export interface ViewOption {
  value: ViewType;
  icon: string;
  label: string;
}

@Component({
  selector: 'app-view-toggle',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="view-toggle">
      <div
        class="toggle-indicator"
        [class.grid-active]="currentView() === 'grid'"
      ></div>
      @for (option of options(); track option.value) {
        <button
          type="button"
          class="toggle-button"
          [class.active]="currentView() === option.value"
          [title]="option.label"
          [attr.aria-label]="option.label"
          (click)="viewChange.emit(option.value)"
        >
          <span class="material-symbols-outlined">{{ option.icon }}</span>
        </button>
      }
    </div>
  `,
  styles: [
    `
      .view-toggle {
        display: inline-flex;
        background: var(--md-sys-color-surface-container);
        border-radius: 20px;
        padding: 4px;
        gap: 2px;
        border: 1px solid var(--md-sys-color-outline-variant);
        position: relative;
        overflow: hidden;
      }

      .toggle-indicator {
        position: absolute;
        top: 4px;
        left: 4px;
        width: 44px;
        height: calc(100% - 8px);
        background: var(--md-sys-color-secondary-container);
        border-radius: 16px;
        transition: transform var(--md-sys-motion-expressive-fast);
        box-shadow: 0 1px 3px rgba(from var(--md-sys-color-shadow) r g b / 0.15);
        z-index: 1;
        transform: translateX(0);
      }

      .toggle-indicator.grid-active {
        transform: translateX(46px);
      }

      .toggle-button {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 8px 12px;
        border-radius: 16px;
        border: none;
        background: transparent;
        color: var(--md-sys-color-on-surface-variant);
        cursor: pointer;
        transition: color var(--md-sys-motion-standard-effects-fast);
        white-space: nowrap;
        min-width: 44px;
        position: relative;
        z-index: 2;
      }

      .toggle-button:hover {
        color: var(--md-sys-color-on-surface);
      }

      .toggle-button.active {
        color: var(--md-sys-color-on-secondary-container);
      }

      .toggle-button .material-symbols-outlined {
        font-size: 18px;
        width: 18px;
        height: 18px;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ViewToggleComponent {
  currentView = input.required<ViewType>();
  options = input<ViewOption[]>([
    { value: 'table', icon: 'table_rows', label: 'Tabellenansicht' },
    { value: 'grid', icon: 'grid_view', label: 'Kartenansicht' },
  ]);

  viewChange = output<ViewType>();
}
